import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, MessageCircle, Users, ArrowUpRight, Mail, Share2, Sparkles, Check, Crown } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Footer } from "@/components/Footer";
import { LegalNotes } from "@/components/LegalNotes";
import { ActivityTicker } from "@/components/ActivityTicker";
import { Countdown } from "@/components/Countdown";
import { FoundingPill } from "@/components/FoundingPill";
import { Leaderboard } from "@/components/Leaderboard";
import { FoundingMember } from "@/components/FoundingMember";
import { FoundingWall } from "@/components/FoundingWall";
import { joinWaitlist, getWaitlistUser, getWaitlistStats } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { track } from "@/lib/analytics";
import { TIERS, tierForCount, nextTier } from "@/lib/tiers";

const HERO_IMG =
  "https://images.unsplash.com/photo-1770671822218-36a1dba8f58f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxhZHJpYXRpYyUyMGNvYXN0JTIwbHV4dXJ5JTIwaG90ZWx8ZW58MHx8fHwxNzg0NDA1Mjc5fDA&ixlib=rb-4.1.0&q=85";

const STORAGE_KEY = "suitecrowd_referral_code";

export default function LandingPage() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({ total: 0, displayed_total: 0, waitlist_closes_at: null });
  const [loading, setLoading] = useState(false);
  const [referrer, setReferrer] = useState(null);
  const referredBy = params.get("ref") || null;

  useSeo({
    title: "SuiteCrowd — Finanzia il lusso. Prenota il futuro.",
    description:
      "Club privato di co-finanziamento turistico. Partecipa alla ristrutturazione di hotel 5 stelle con una quota simbolica e assicurati un soggiorno futuro. Fondi in escrow, rimborso garantito.",
    canonical: "https://suitecrowd.com/",
  });

  useEffect(() => {
    getWaitlistStats().then(setStats).catch(() => {});
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      getWaitlistUser(saved)
        .then(setUser)
        .catch(() => localStorage.removeItem(STORAGE_KEY));
    }
    if (referredBy) {
      getWaitlistUser(referredBy)
        .then((r) => {
          setReferrer(r);
          track("referral_landed", { code: referredBy, referrer_position: r.position });
        })
        .catch(() => setReferrer(null));
    }
  }, [referredBy]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const data = await joinWaitlist(email, referredBy);
      setUser(data);
      localStorage.setItem(STORAGE_KEY, data.referral_code);
      track("waitlist_joined", {
        position: data.position,
        referred: !!referredBy,
      });
      toast.success("Sei nel club. Ti abbiamo inviato la conferma via email.");
    } catch (err) {
      toast.error("Impossibile completare l'iscrizione. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    localStorage.removeItem(STORAGE_KEY);
    setUser(null);
    setEmail("");
  };

  const inviteUrl = user
    ? `${window.location.origin}/?ref=${user.referral_code}`
    : "";

  const copyLink = () => {
    navigator.clipboard.writeText(inviteUrl);
    track("referral_link_copied");
    toast.success("Link copiato. Incollalo dove vuoi.");
  };

  const whatsappShare = () => {
    const msg = encodeURIComponent(
      `Sto entrando in SuiteCrowd — club privato di co-finanziamento hotel 5 stelle in Italia. Sono in posizione #${user?.position || "?"}. Se ti iscrivi col mio link salti la coda: ${inviteUrl}`
    );
    track("referral_share", { channel: "whatsapp" });
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-white text-[#121212]">
      {/* Hero */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen w-full overflow-hidden grain"
      >
        <img
          src={HERO_IMG}
          alt="Hotel di lusso sulla costa"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/70" />

        {/* Top nav */}
        <nav className="relative z-10 flex justify-between items-center px-6 md:px-12 py-8">
          <Logo variant="light" size="md" />
          <div className="flex items-center gap-6">
            <Link
              to="/come-funziona"
              data-testid="nav-how"
              className="hidden md:inline text-[11px] tracking-[0.28em] uppercase text-white/80 hover:text-white transition-colors"
            >
              Come Funziona
            </Link>
            <Link
              to="/hotel-pilota"
              data-testid="nav-hotel-pilot"
              className="hidden md:inline text-[11px] tracking-[0.28em] uppercase text-white/80 hover:text-white transition-colors"
            >
              Hotel Pilota
            </Link>
            <div className="text-[11px] tracking-[0.28em] uppercase text-white/60">
              <span data-testid="waitlist-total">{(stats.displayed_total || 0).toLocaleString("it-IT")}</span> membri
            </div>
          </div>
        </nav>

        {/* Center content */}
        <div className="relative z-10 flex flex-col items-center justify-center px-6 md:px-12 pt-16 pb-32 min-h-[80vh]">
          <div className="max-w-2xl w-full text-center text-white">
            <div className="sc-overline sc-fade-up" style={{ color: "rgba(255,255,255,0.7)" }}>
              Club privato · Solo su invito
            </div>

            {!user && (
              <div className="mt-6 sc-fade-up sc-delay-1 flex flex-wrap items-center justify-center gap-3">
                <ActivityTicker />
                <Countdown target={stats.waitlist_closes_at} />
                <FoundingPill />
              </div>
            )}

            {!user && referrer && (
              <div
                data-testid="referral-invite-banner"
                className="mt-8 sc-fade-up sc-delay-2 max-w-md mx-auto border border-white/25 bg-white/5 backdrop-blur-md p-5"
              >
                <div className="sc-overline mb-2" style={{ color: "rgba(255,255,255,0.55)" }}>
                  {referrer.is_founding_member ? "Un Founding Member ti invita" : "Un membro ti invita"}
                </div>
                <div className="text-white text-sm">
                  <span className="font-mono">
                    {(() => {
                      const [n, d] = referrer.email.split("@");
                      const [dn, _, tld] = d.match(/^([^.]+)(\.)(.+)$/)?.slice(1) || [d, "", ""];
                      return `${(n[0] || "").toUpperCase()}${(n.slice(1, 2) || "")}***@${(dn[0] || "").toUpperCase()}***${tld ? "." + tld : ""}`;
                    })()}
                  </span>
                  {" "}è in posizione{" "}
                  <span className="text-white font-medium">#{referrer.position}</span>
                  {" "}·{" "}
                  <span className="text-white font-medium">
                    {referrer.referrals_count} {referrer.referrals_count === 1 ? "invito già" : "inviti già"}
                  </span>
                </div>
                <div className="mt-3 text-[12px] text-white/60 leading-relaxed">
                  Iscriviti con la sua email condivisa qui sotto — {referrer.is_founding_member
                    ? "avanzerà nel Podio Founding e potrà vincere il soggiorno gratuito alla chiusura."
                    : "gli farai scalare 5 posizioni verso i tier con perks esclusive."}
                </div>
              </div>
            )}

            {!user ? (
              <>
                <h1
                  data-testid="hero-title"
                  className="font-display font-light text-5xl md:text-7xl leading-[1.05] mt-6 mb-6 sc-fade-up sc-delay-1"
                  style={{ letterSpacing: "-0.03em" }}
                >
                  Finanzia il lusso.
                  <br />
                  <span className="italic font-extralight">Prenota il futuro.</span>
                </h1>
                <p
                  className="text-white/70 max-w-lg mx-auto text-[15px] leading-relaxed mb-14 sc-fade-up sc-delay-2"
                >
                  Co-finanzia hotel 5 stelle in ristrutturazione. Assicurati un
                  soggiorno futuro con una quota simbolica. Zero speculazione,
                  solo accesso privilegiato.
                </p>

                <form
                  data-testid="waitlist-form"
                  onSubmit={submit}
                  className="max-w-md mx-auto sc-fade-up sc-delay-3"
                >
                  <div className="flex items-end gap-4">
                    <div className="flex-1 text-left">
                      <label className="sc-overline block mb-1" style={{ color: "rgba(255,255,255,0.5)" }}>
                        Email
                      </label>
                      <input
                        data-testid="waitlist-email-input"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="tua.email@dominio.com"
                        className="sc-input"
                      />
                    </div>
                    <button
                      data-testid="waitlist-submit-btn"
                      type="submit"
                      disabled={loading}
                      className="sc-btn-primary bg-white text-[#0B192C] hover:bg-white/90"
                    >
                      {loading ? "..." : "Entra"}
                    </button>
                  </div>
                  {referredBy && (
                    <div className="mt-4 text-[11px] tracking-[0.2em] uppercase text-white/60">
                      Invito da <span className="text-white">{referredBy}</span>
                    </div>
                  )}
                  <p className="mt-6 text-[11px] text-white/40 tracking-wide">
                    Iscrivendoti accetti le nostre note legali e trasparenza sui fondi (escrow).
                  </p>
                </form>
              </>
            ) : (
              <ReferralView user={user} onReset={reset} inviteUrl={inviteUrl} onCopy={copyLink} onShare={whatsappShare} />
            )}
          </div>
        </div>

        {/* Bottom marker */}
        <div className="absolute bottom-8 left-6 md:left-12 right-6 md:right-12 z-10 flex justify-between items-end text-white/50 text-[11px] tracking-[0.28em] uppercase">
          <span>Grand Hotel · Adriatico</span>
          <span>Anteprima 2026</span>
        </div>
      </section>

      {/* Concept strip */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-3 gap-16">
        {[
          { k: "Co-finanziamento", v: "Contribuisci alla rinascita di strutture di prestigio con quote accessibili." },
          { k: "Conto Escrow", v: "I fondi sono congelati fino al raggiungimento del target o dell'avanzamento certificato." },
          { k: "Soggiorno Garantito", v: "Voucher da 4 giorni per l'anno successivo al completamento dei lavori." },
        ].map((item, i) => (
          <div key={i} data-testid={`concept-${i}`} className="space-y-4">
            <div className="sc-overline">0{i + 1}</div>
            <h3 className="font-display text-2xl font-light" style={{ letterSpacing: "-0.02em" }}>
              {item.k}
            </h3>
            <p className="text-sm text-neutral-600 leading-relaxed">{item.v}</p>
          </div>
        ))}
      </section>

      {/* Pilot preview */}
      <section className="bg-[#F7F7F8] py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-end gap-8 mb-16">
          <div>
            <div className="sc-overline mb-4">Anteprima Progetto Pilota</div>
            <h2 className="font-display font-light text-4xl md:text-5xl" style={{ letterSpacing: "-0.02em" }}>
              Grand Hotel Olivier Prince
            </h2>
            <p className="text-neutral-500 mt-3">Roseto degli Abruzzi · Adriatico</p>
          </div>
          <Link
            to="/hotel-pilota"
            data-testid="cta-view-pilot"
            className="sc-btn-ghost inline-flex items-center gap-2"
          >
            Scopri il progetto <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>

      {/* Leaderboard */}
      <Leaderboard />

      {/* Founding Member */}
      <FoundingMember user={user} />

      {/* Founding Wall + Podium */}
      <FoundingWall />

      {/* Hotelier CTA */}
      <section className="bg-[#0B192C] text-white py-20 md:py-28 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="sc-overline mb-6" style={{ color: "rgba(255,255,255,0.6)" }}>
            Sei il proprietario di un hotel?
          </div>
          <h2
            className="font-display font-light text-4xl md:text-5xl mb-6"
            style={{ letterSpacing: "-0.02em" }}
          >
            Porta il tuo progetto di ristrutturazione al club.
          </h2>
          <p className="text-white/70 max-w-xl mx-auto text-[15px] leading-relaxed mb-10">
            Crea il tuo profilo, carica le foto della struttura, aggiorna l'avanzamento
            dei lavori. I nostri membri co-finanziano il tuo rilancio.
          </p>
          <Link
            to="/proponi"
            data-testid="cta-propose-hotel"
            className="sc-btn-primary bg-white text-[#0B192C] hover:bg-white/90 inline-flex items-center gap-2"
          >
            Proponi il tuo hotel <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>

      {/* Legal excerpt in-page */}
      <section className="bg-white py-20 px-6 md:px-12 border-t border-[#E5E5E5]">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#121212] p-10 md:p-14">
            <LegalNotes />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ReferralView({ user, onReset, inviteUrl, onCopy, onShare }) {
  const isFounding = !!user.is_founding_member;
  const tier = isFounding ? TIERS[TIERS.length - 1] : tierForCount(user.referrals_count);
  const next = isFounding ? null : nextTier(user.referrals_count);
  const remaining = next ? next.min - user.referrals_count : 0;
  const tierIdx = TIERS.findIndex((t) => t.key === tier.key);
  // Progress within the current tier segment
  const totalTiers = TIERS.length;
  const overallProgress = next
    ? ((tierIdx + (user.referrals_count - tier.min) / (next.min - tier.min)) / totalTiers) * 100
    : 100;

  const shareVia = (kind) => {
    const msg = `Sono nel club di SuiteCrowd — co-finanziamento hotel 5 stelle in Italia. Sono in posizione #${user.position}. Entra col mio link e ti trascino su nella coda: ${inviteUrl}`;
    if (kind === "whatsapp") window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
    else if (kind === "email") window.open(`mailto:?subject=${encodeURIComponent("Entra con me in SuiteCrowd")}&body=${encodeURIComponent(msg)}`);
    else if (kind === "linkedin") {
      const linkedinText = `Ho appena preso il mio posto in SuiteCrowd — il primo club italiano di co-finanziamento turistico per hotel 5 stelle in ristrutturazione. Fondi in escrow, soggiorno futuro garantito, zero speculazione. Sono #${user.position}. ${inviteUrl}`;
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(inviteUrl)}&summary=${encodeURIComponent(linkedinText)}`, "_blank");
    }
    else if (kind === "twitter") {
      const tweetText = `Sono #${user.position} nel club di @SuiteCrowd 🏛️ Co-finanziamento hotel 5⭐ in Italia. Entra col mio link e salta la coda:`;
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(inviteUrl)}`, "_blank");
    }
    else if (kind === "native" && navigator.share) {
      navigator.share({ title: "SuiteCrowd", text: msg, url: inviteUrl }).catch(() => {});
    }
  };

  return (
    <div data-testid="referral-view" className="sc-fade-up">
      <div className="sc-overline mb-6 inline-flex items-center gap-2" style={{ color: "rgba(255,255,255,0.6)" }}>
        {isFounding && <Crown size={12} />} Sei nel club · Tier {tier.name}
      </div>
      <div className="mb-3 text-white/60 text-sm">La tua posizione</div>
      <div
        data-testid="referral-position"
        className="font-display font-extralight text-[88px] md:text-[128px] leading-none mb-6"
        style={{ letterSpacing: "-0.05em" }}
      >
        #{user.position}
      </div>

      {/* Tier progress */}
      <div data-testid="tier-progress" className="max-w-md mx-auto mb-10">
        <div className="flex justify-between text-[10px] tracking-[0.28em] uppercase text-white/50 mb-3">
          {TIERS.map((t) => (
            <span
              key={t.key}
              className={t.key === tier.key ? "text-white" : ""}
              style={{ minWidth: 40 }}
            >
              {t.name}
            </span>
          ))}
        </div>
        <div className="h-[2px] w-full bg-white/15 relative overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-white transition-all duration-700"
            style={{ width: `${overallProgress}%` }}
          />
        </div>
        <div className="mt-4 text-[13px] text-white/70">
          {next ? (
            <>
              Ti mancano{" "}
              <span className="text-white font-medium">{remaining}</span>{" "}
              {remaining === 1 ? "invito" : "inviti"} per il tier{" "}
              <span className="text-white">{next.name}</span>
            </>
          ) : (
            <>Hai raggiunto il tier massimo — <span className="text-white">Platino</span></>
          )}
        </div>
      </div>

      {/* Tier perks */}
      <div className="max-w-md mx-auto mb-10 border border-white/10 bg-white/5 backdrop-blur-sm p-5 text-left">
        <div className="sc-overline mb-3 inline-flex items-center gap-2" style={{ color: "rgba(255,255,255,0.6)" }}>
          <Sparkles size={12} /> Il tuo tier
        </div>
        <ul className="space-y-2 text-[13px] text-white/80">
          {tier.perks.map((p, i) => (
            <li key={i} className="flex items-start gap-2">
              <Check size={13} className="mt-1 flex-shrink-0 text-white" strokeWidth={1.5} />
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </div>

      <p className="text-white/70 text-[14px] max-w-md mx-auto mb-10 leading-relaxed">
        Hai già invitato <span className="text-white">{user.referrals_count}</span>{" "}
        {user.referrals_count === 1 ? "persona" : "persone"} · Ogni invito ti fa scalare 5 posizioni.
      </p>

      <div className="max-w-md mx-auto space-y-4">
        <div className="text-left">
          <div className="sc-overline mb-2" style={{ color: "rgba(255,255,255,0.5)" }}>
            Il tuo link di invito
          </div>
          <div className="flex items-center gap-2 border border-white/20 bg-black/20 backdrop-blur-sm px-4 py-3">
            <input
              data-testid="invite-link-input"
              readOnly
              value={inviteUrl}
              className="flex-1 bg-transparent border-none outline-none text-white text-sm"
            />
            <button
              data-testid="copy-link-btn"
              onClick={onCopy}
              className="text-white/70 hover:text-white transition-colors"
              aria-label="Copia link"
            >
              <Copy size={16} />
            </button>
          </div>
        </div>

        <button
          data-testid="whatsapp-share-btn"
          onClick={() => shareVia("whatsapp")}
          className="sc-btn-primary bg-white text-[#0B192C] hover:bg-white/90 w-full inline-flex items-center justify-center gap-3"
        >
          <MessageCircle size={16} /> Condividi su WhatsApp
        </button>

        <div className="grid grid-cols-2 gap-3">
          <button
            data-testid="linkedin-share-btn"
            onClick={() => shareVia("linkedin")}
            className="sc-btn-ghost inline-flex items-center justify-center gap-2"
            style={{ color: "#ffffff", borderColor: "rgba(255,255,255,0.25)" }}
          >
            LinkedIn
          </button>
          <button
            data-testid="twitter-share-btn"
            onClick={() => shareVia("twitter")}
            className="sc-btn-ghost inline-flex items-center justify-center gap-2"
            style={{ color: "#ffffff", borderColor: "rgba(255,255,255,0.25)" }}
          >
            X / Twitter
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            data-testid="email-share-btn"
            onClick={() => shareVia("email")}
            className="sc-btn-ghost inline-flex items-center justify-center gap-2"
            style={{ color: "#ffffff", borderColor: "rgba(255,255,255,0.25)" }}
          >
            <Mail size={14} /> Email
          </button>
          {typeof navigator !== "undefined" && navigator.share ? (
            <button
              data-testid="native-share-btn"
              onClick={() => shareVia("native")}
              className="sc-btn-ghost inline-flex items-center justify-center gap-2"
              style={{ color: "#ffffff", borderColor: "rgba(255,255,255,0.25)" }}
            >
              <Share2 size={14} /> Condividi
            </button>
          ) : (
            <button
              onClick={onCopy}
              className="sc-btn-ghost inline-flex items-center justify-center gap-2"
              style={{ color: "#ffffff", borderColor: "rgba(255,255,255,0.25)" }}
            >
              <Copy size={14} /> Copia link
            </button>
          )}
        </div>

        <button
          data-testid="referral-reset-btn"
          onClick={onReset}
          className="text-[11px] tracking-[0.24em] uppercase text-white/50 hover:text-white/80 transition-colors mt-6"
        >
          Iscrivi un'altra email
        </button>
      </div>

      <div className="mt-14 inline-flex items-center gap-2 text-white/60 text-xs tracking-[0.2em] uppercase">
        <Users size={13} /> Membro #{user.position}
      </div>
    </div>
  );
}
